import {
  computed,
  DestroyRef,
  inject,
  Injectable,
  signal,
} from '@angular/core';
import { VmPatient, VmSettings, Voicemail } from '@models';
import { VOICEMAIL_GATEWAY, VoicemailGatewayError } from './voicemail.gateway';

interface PendingMutation {
  signature: string;
  promise: Promise<boolean>;
}

/** Feature-scoped state. Read requests cannot overwrite a more recent user action. */
@Injectable()
export class VoicemailStore {
  private readonly gateway = inject(VOICEMAIL_GATEWAY);
  private readonly destroyRef = inject(DestroyRef);
  private readonly _messages = signal<Voicemail[]>([]);
  private readonly _settings = signal<VmSettings | null>(null);
  private readonly _loading = signal(false);
  private readonly _error = signal<string | null>(null);
  private readonly _settingsError = signal<string | null>(null);
  private readonly _settingsRefreshing = signal(false);
  private readonly _mutationError = signal<string | null>(null);
  private readonly _mutationErrorKey = signal<string | null>(null);
  private readonly _busyIds = signal<readonly string[]>([]);
  private readonly pending = new Map<string, PendingMutation>();
  private readonly versions = new Map<string, number>();
  private loadSequence = 0;
  private settingsSequence = 0;
  private searchSequence = 0;
  private sessionEpoch = 0;
  private destroyed = false;

  readonly messages = this._messages.asReadonly();
  readonly settings = this._settings.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  /** Failure of a settings-only refresh; separate from `error` so an inbox failure is not masked. */
  readonly settingsError = this._settingsError.asReadonly();
  readonly settingsRefreshing = this._settingsRefreshing.asReadonly();
  readonly mutationError = this._mutationError.asReadonly();
  readonly mutationErrorKey = this._mutationErrorKey.asReadonly();
  readonly busyIds = this._busyIds.asReadonly();
  readonly unreadCount = computed(
    () => this.messages().filter(item => item.unread).length
  );

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.destroyed = true;
      this.reset();
    });
  }

  /** Clear provider-owned state before changing the authenticated provider. */
  reset(): void {
    this.sessionEpoch++;
    this.loadSequence++;
    this.settingsSequence++;
    this.searchSequence++;
    this.pending.clear();
    this.versions.clear();
    this._messages.set([]);
    this._settings.set(null);
    this._loading.set(false);
    this._settingsRefreshing.set(false);
    this._error.set(null);
    this._settingsError.set(null);
    this._mutationError.set(null);
    this._mutationErrorKey.set(null);
    this._busyIds.set([]);
  }

  async load(): Promise<void> {
    if (this.destroyed) return;
    const sequence = ++this.loadSequence;
    // A full load supersedes any settings-only refresh still in flight.
    this.settingsSequence++;
    const versions = new Map(this.versions);
    this._loading.set(true);
    this._error.set(null);
    this._mutationError.set(null);
    this._mutationErrorKey.set(null);
    try {
      const [messages, settings] = await Promise.all([
        this.gateway.list(),
        this.gateway.getSettings(),
      ]);
      if (this.destroyed || sequence !== this.loadSequence) return;

      // A reload may have captured its data before a mutation finished.
      const previous = new Map(this.messages().map(item => [item.id, item]));
      this._messages.set(
        messages.map(item =>
          this.changedSince(item.id, versions)
            ? (previous.get(item.id) ?? item)
            : item
        )
      );
      if (!this.settingsChangedSince(versions)) this._settings.set(settings);
      this._settingsError.set(null);
    } catch (error: unknown) {
      if (!this.destroyed && sequence === this.loadSequence) {
        this._error.set(
          this.message(error, 'Calls could not be loaded. Please try again.')
        );
      }
    } finally {
      if (!this.destroyed && sequence === this.loadSequence)
        this._loading.set(false);
    }
  }

  /**
   * The transcript for one voicemail. Not cached and not folded into the message list: reading it is
   * an audited access to patient content, so it is fetched when somebody opens the message and the
   * record says which transcripts were actually read.
   */
  async transcriptFor(id: string): Promise<string> {
    const transcript = await this.gateway.getTranscript(id);

    return transcript.text;
  }

  reload(): Promise<void> {
    return this.load();
  }

  /**
   * Ask the carrier about an activation whose outcome was never seen, and adopt whatever it says.
   *
   * Deliberately separate from reading settings. Reconciling can bind a number and activate a line,
   * so it happens when the provider asks for it and at no other time.
   */
  async checkActivationStatus(): Promise<void> {
    const epoch = this.sessionEpoch;

    await this.mutate('settings', 'reconcile', async () => {
      const settings = await this.gateway.reconcile();

      return () => {
        if (this.isCurrentSession(epoch)) this._settings.set(settings);
      };
    });
  }

  /**
   * Re-read settings alone, in the background. The derived booking link changes outside this
   * feature (Scheduling type, landing page publication), so a screen that comes back to settings
   * cannot trust the cached snapshot, yet it has no use for the voicemail list and should keep
   * rendering what it has: neither `settings` nor `loading` is touched until the read completes.
   */
  async reloadSettings(): Promise<void> {
    if (this.destroyed) return;
    const sequence = ++this.settingsSequence;
    const epoch = this.sessionEpoch;
    const versions = new Map(this.versions);
    this._settingsRefreshing.set(true);
    try {
      const settings = await this.gateway.getSettings();
      if (!this.isCurrentSession(epoch) || sequence !== this.settingsSequence)
        return;
      // A save that finished meanwhile is newer than this read.
      if (!this.settingsChangedSince(versions)) this._settings.set(settings);
      this._settingsError.set(null);
    } catch (error: unknown) {
      if (this.isCurrentSession(epoch) && sequence === this.settingsSequence) {
        this._settingsError.set(
          this.message(
            error,
            'Voicemail settings could not be refreshed. Please try again.'
          )
        );
      }
    } finally {
      if (this.isCurrentSession(epoch) && sequence === this.settingsSequence)
        this._settingsRefreshing.set(false);
    }
  }

  updateSettings(changes: Partial<VmSettings>): Promise<boolean> {
    const current = this.settings();
    if (!current) return Promise.resolve(false);
    const next = { ...current, ...changes };
    return this.mutate('settings', `save:${JSON.stringify(next)}`, async () => {
      const saved = await this.gateway.saveSettings(next);
      // The website-number switch has its own writer. A greeting save that was read before the
      // switch was flipped must not put the old choice back.
      return () =>
        this._settings.update(settings => ({
          ...saved,
          useVoicemailNumberOnWebsite:
            settings?.useVoicemailNumberOnWebsite ??
            saved.useVoicemailNumberOnWebsite,
        }));
    });
  }

  /**
   * Its own mutation key, so the switch neither waits on nor blocks a greeting save. On failure the
   * settings are left as they were, which is what puts the switch back.
   */
  setWebsiteNumber(useVoicemailNumber: boolean): Promise<boolean> {
    if (!this.settings()) return Promise.resolve(false);
    return this.mutate(
      'website-number',
      `website-number:${useVoicemailNumber}`,
      async () => {
        const saved = await this.gateway.setWebsiteNumber(useVoicemailNumber);
        // Only the field this writer owns, so an unsaved-then-saved greeting is not overwritten.
        return () =>
          this._settings.update(settings =>
            settings
              ? {
                  ...settings,
                  useVoicemailNumberOnWebsite:
                    saved.useVoicemailNumberOnWebsite,
                }
              : saved
          );
      }
    );
  }

  async activate(): Promise<boolean> {
    const epoch = this.sessionEpoch;
    const current = this.settings();
    if (!current) return false;
    if (current.state === 'active') return true;
    if (current.state === 'checking' || current.state === 'support-required')
      return false;
    const succeeded = await this.mutate('settings', 'activate', async () => {
      this._settings.set({ ...current, state: 'provisioning' });
      try {
        const activated = await this.gateway.activate();
        return () => this._settings.set(activated);
      } catch (error: unknown) {
        // Only an outcome nobody knows goes to "checking" -- that state exists to say "a purchase
        // may have happened", and its only exit is asking the carrier. A definite refusal is not
        // that: parking it there hides the retry the provider actually needs behind a Check status
        // button that can only ever answer "nothing was bought".
        if (this.isCurrentSession(epoch)) {
          this._settings.set({
            ...current,
            state: isDefiniteActivationFailure(error)
              ? current.state
              : 'checking',
          });
        }
        throw error;
      }
    });
    return (
      succeeded &&
      this.isCurrentSession(epoch) &&
      this.settings()?.state === 'active'
    );
  }

  markRead(id: string): Promise<boolean> {
    return this.setUnread(id, false);
  }

  markUnread(id: string): Promise<boolean> {
    return this.setUnread(id, true);
  }

  linkPatient(id: string, patientId: string | null): Promise<boolean> {
    return this.mutateMessage(id, `link:${patientId}`, () =>
      this.gateway.linkPatient(id, patientId)
    );
  }

  async searchPatients(query: string): Promise<VmPatient[]> {
    if (this.destroyed) return [];
    const sequence = ++this.searchSequence;
    try {
      const patients = await this.gateway.searchPatients(query);
      return this.destroyed || sequence !== this.searchSequence ? [] : patients;
    } catch (error: unknown) {
      if (this.destroyed || sequence !== this.searchSequence) return [];
      // Preserve the distinction between an unsuccessful search and zero matches.
      throw error;
    }
  }

  private setUnread(id: string, unread: boolean): Promise<boolean> {
    const message = this.messages().find(item => item.id === id);
    if (!message) return Promise.resolve(false);
    if (message.unread === unread && !this.pending.has(id))
      return Promise.resolve(true);
    return this.mutateMessage(id, `unread:${unread}`, () =>
      this.gateway.markUnread(id, unread)
    );
  }

  private mutateMessage(
    id: string,
    signature: string,
    request: () => Promise<Voicemail>
  ): Promise<boolean> {
    return this.mutate(id, signature, async () => {
      const updated = await request();
      return () =>
        this._messages.update(items =>
          items.map(item => (item.id === id ? updated : item))
        );
    });
  }

  private mutate(
    key: string,
    signature: string,
    request: () => Promise<() => void>
  ): Promise<boolean> {
    const existing = this.pending.get(key);
    if (existing)
      return existing.signature === signature
        ? existing.promise
        : Promise.resolve(false);
    if (this.destroyed) return Promise.resolve(false);
    const epoch = this.sessionEpoch;
    this._mutationError.set(null);
    this._mutationErrorKey.set(null);
    this.bumpVersion(key);
    const promise = Promise.resolve().then(async () => {
      try {
        // A reset before the queued microtask runs must not dispatch an old
        // provider's action using the new provider's authentication context.
        if (!this.isCurrentSession(epoch)) return false;
        const apply = await request();
        if (!this.isCurrentSession(epoch)) return false;
        apply();
        return true;
      } catch (error: unknown) {
        if (this.isCurrentSession(epoch)) {
          this._mutationErrorKey.set(key);
          this._mutationError.set(
            this.message(
              error,
              'The change could not be saved. Please try again.'
            )
          );
        }
        return false;
      } finally {
        if (
          this.isCurrentSession(epoch) &&
          this.pending.get(key)?.promise === promise
        ) {
          this.bumpVersion(key);
          this.pending.delete(key);
          this._busyIds.set([...this.pending.keys()]);
        }
      }
    });
    this.pending.set(key, { signature, promise });
    this._busyIds.set([...this.pending.keys()]);
    return promise;
  }

  private isCurrentSession(epoch: number): boolean {
    return !this.destroyed && epoch === this.sessionEpoch;
  }

  private changedSince(key: string, versions: Map<string, number>): boolean {
    return (
      this.pending.has(key) || this.versions.get(key) !== versions.get(key)
    );
  }

  /** Either settings write — the form save or the website-number switch — is newer than a read. */
  private settingsChangedSince(versions: Map<string, number>): boolean {
    return (
      this.changedSince('settings', versions) ||
      this.changedSince('website-number', versions)
    );
  }

  private bumpVersion(key: string): void {
    this.versions.set(key, (this.versions.get(key) ?? 0) + 1);
  }

  private message(error: unknown, fallback: string): string {
    return error instanceof Error ? error.message : fallback;
  }
}

/**
 * Whether an activation failure is a settled answer rather than an unknown one. The codes come from
 * the API; anything unrecognised is treated as unknown, because the expensive mistake is assuming a
 * purchase did not happen when it did.
 */
function isDefiniteActivationFailure(error: unknown): boolean {
  const code = error instanceof VoicemailGatewayError ? error.code : null;

  return (
    code === 'VOICEMAIL_DESTINATION_REQUIRED' ||
    code === 'VOICEMAIL_NUMBER_UNAVAILABLE' ||
    code === 'VOICEMAIL_UNAVAILABLE' ||
    code === 'VOICEMAIL_LINE_NOT_FOUND' ||
    code === 'VOICEMAIL_ACTIVATION_IN_PROGRESS'
  );
}
